import type { VerifyAccountAPIRequest, VerifyAccountAPIResponse } from './types'
import defaultFetch from 'isomorphic-unfetch'
import { appendContextItemId } from '@/scopes/common/domain/policies/contextItemId'
import { VerifyAccountRequestError } from '../domain/VerifyAccountRequestError'
import { mapVerifyAccountResponse } from './verifyAccountMapper'

interface VerifyAccountApiDependencies {
  env?: { NEXT_PUBLIC_API_BASE_URL?: string }
  fetch?: typeof defaultFetch
  contextItemId?: string
}

export function createVerifyAccountApiClient({
  env = { NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL },
  fetch = defaultFetch,
  contextItemId = '',
}: VerifyAccountApiDependencies = {}) {
  async function verifyAccount(token: string) {
    if (!env.NEXT_PUBLIC_API_BASE_URL)
      throw new VerifyAccountRequestError('missingConfiguration')

    const payload: VerifyAccountAPIRequest = { token }
    let response: Response
    try {
      response = await fetch(appendContextItemId(`${env.NEXT_PUBLIC_API_BASE_URL}/auth/verifyaccount`, contextItemId), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
    }
    catch {
      throw new VerifyAccountRequestError('networkError')
    }

    if (response.status !== 200 && response.status !== 400)
      throw new VerifyAccountRequestError('httpError', response.status)

    let body: VerifyAccountAPIResponse
    try {
      body = await response.json()
    }
    catch {
      throw new VerifyAccountRequestError('invalidResponse', response.status)
    }

    if (!body || typeof body !== 'object' || Array.isArray(body))
      throw new VerifyAccountRequestError('invalidResponse', response.status)

    return mapVerifyAccountResponse(response.status, body)
  }

  return { verifyAccount }
}
